import React from "react"

import globalStyles from "../../../../App.module.scss";

export default function FilePermissions() {
    return (
        <div className={globalStyles.article}>
            <h1>Linux file permissions</h1>

            <p>Every file and directory has three sets of permissions, one each for the <b>owner</b>, the
                <b> group</b> and <b>everyone else</b>. Each set can grant <b>r</b>ead, <b>w</b>rite and
                e<b>x</b>ecute.</p>

            <pre className={globalStyles.code}>
    <b>user@host:~$</b> ls -l notes.txt<br/>
    -rw-r--r-- 1 julian staff 128 Sep 7 10:00 notes.txt<br/>
            </pre>

            <p>The first character is the file type ('-' for a regular file, 'd' for directory, 'l' for a
                symlink). The next nine characters are three rwx triplets, in order: owner, group, other.</p>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '330px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Octal values</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>4</b></td>
                        <td>read (r)</td>
                    </tr>
                    <tr>
                        <td><b>2</b></td>
                        <td>write (w)</td>
                    </tr>
                    <tr>
                        <td><b>1</b></td>
                        <td>execute (x)</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <p>Add the values together for each triplet, e.g. <b>rwxr-xr--</b> is <b>754</b> (7=4+2+1,
                5=4+1, 4=4).</p>

            <pre className={globalStyles.code}>
    <b>user@host:~$</b> chmod 754 script.sh<br/>
    <b>user@host:~$</b> chmod u+x,g-w,o=r script.sh <i># symbolic form</i><br/>
            </pre>

            <p>Change ownership with <b>chown</b> and <b>chgrp</b>:</p>

            <pre className={globalStyles.code}>
    <b>root@host:~#</b> chown julian:staff notes.txt<br/>
    <b>root@host:~#</b> chgrp staff notes.txt<br/>
    <b>root@host:~#</b> chown -R julian:staff /var/www/app <i># recursive</i><br/>
            </pre>

            <h3>Special permission bits</h3>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '330px'}}>
                    <thead>
                    <tr>
                        <th colSpan="3">Special bits</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>setuid</b> (4000)</td>
                        <td><code>chmod u+s file</code></td>
                        <td>Runs the executable as its owner, not as the invoking user (e.g. <code>/usr/bin/passwd</code>).</td>
                    </tr>
                    <tr>
                        <td><b>setgid</b> (2000)</td>
                        <td><code>chmod g+s dir</code></td>
                        <td>On a directory, new files inherit the directory's group instead of the creator's.</td>
                    </tr>
                    <tr>
                        <td><b>sticky bit</b> (1000)</td>
                        <td><code>chmod +t dir</code></td>
                        <td>In a shared directory (e.g. <code>/tmp</code>), only the file's owner (or root) can delete or
                            rename it.
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <p>Special bits are prefixed to the normal octal mode, so 'rwsr-xr-x' with setuid is <b>4755</b>.</p>

            <pre className={globalStyles.code}>
    <b>root@host:~#</b> chmod 4755 /usr/local/bin/backup-runner<br/>
    <b>root@host:~#</b> chmod 1777 /srv/shared-uploads <i># sticky bit, world-writable</i><br/>
            </pre>
        </div>
    )
}
